import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Star, Quote, ChevronRight, Shield } from "lucide-react";

const testimonials = [
  {
    id: 1,
    quote: "I was extremely nervous about having surgery, but Dr. Reynolds and the anesthesia team put me completely at ease. They explained everything beforehand and I woke up feeling great with no nausea.",
    rating: 5,
    type: "Verified Patient",
    service: "General Anesthesia",
    verified: true,
  },
  {
    id: 2,
    quote: "The pre-operative consultation was thorough and reassuring. The anesthesiologist took time to understand my concerns about my heart condition and developed a personalized plan.",
    rating: 5,
    type: "Verified Patient",
    service: "Cardiac Risk Management",
    verified: true,
  },
  {
    id: 3,
    quote: "Had my knee surgery here and the nerve block they gave me was incredible. Minimal pain after surgery and I was able to start physical therapy sooner than expected.",
    rating: 5,
    type: "Verified Patient",
    service: "Regional Anesthesia",
    verified: true,
  },
  {
    id: 4,
    quote: "The staff was professional and caring from start to finish. The facility is clean and modern. I felt safe and well-cared for throughout my entire experience.",
    rating: 5,
    type: "Verified Patient",
    service: "Outpatient Surgery",
    verified: true,
  },
  {
    id: 5,
    quote: "My colonoscopy was a breeze thanks to the sedation team. I don't remember a thing and recovered quickly. Highly recommend for anyone nervous about the procedure.",
    rating: 5,
    type: "Verified Patient",
    service: "IV Sedation",
    verified: true,
  },
  {
    id: 6,
    quote: "As a diabetic patient, I appreciated how carefully they managed my care before and during surgery. The anesthesia team coordinated everything perfectly.",
    rating: 5,
    type: "Verified Patient",
    service: "Complex Medical Care",
    verified: true,
  },
  {
    id: 7,
    quote: "The post-operative pain management was excellent. They gave me a nerve block that lasted for hours and I needed minimal pain medication afterward.",
    rating: 5,
    type: "Verified Patient",
    service: "Pain Management",
    verified: true,
  },
  {
    id: 8,
    quote: "I had a bad experience with anesthesia years ago, but this team listened to my concerns and adjusted their approach. Woke up feeling better than I ever have after surgery.",
    rating: 5,
    type: "Verified Patient",
    service: "General Anesthesia",
    verified: true,
  },
  {
    id: 9,
    quote: "Brought my elderly mother here for her cataract surgery. The anesthesiologist was so gentle and patient with her. She came through with flying colors.",
    rating: 5,
    type: "Verified Patient",
    service: "Ophthalmology Support",
    verified: true,
  },
];

export default function Testimonials() {
  return (
    <div data-testid="testimonials-page" className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-vip-black relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 70% 30%, rgba(191, 64, 191, 0.1) 0%, transparent 50%)'
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">Patient Reviews</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none font-bold italic text-white mb-8">
            Patient <span className="gradient-text">Testimonials</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg lg:text-xl max-w-2xl mx-auto">
            Hear from patients about their anesthesia experience 
            at Surgery Center LLC.
          </p>
        </div>
      </section>

      {/* Privacy Notice */}
      <section className="py-8 bg-vip-paper border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-3 text-gray-400">
            <Shield className="w-5 h-5 text-neon-cyan" />
            <p className="text-sm">
              All testimonials are from verified patients. Names and identifying details are protected for privacy.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section data-testid="testimonials-grid" className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Masonry-style grid */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                data-testid={`testimonial-card-${index}`}
                className="break-inside-avoid vip-card glass rounded-2xl p-8 border border-white/10 hover:border-neon-pink/30 relative"
              >
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 opacity-20">
                  <Quote className="w-10 h-10 text-royal-purple" />
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 text-neon-cyan fill-neon-cyan"
                    />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="font-heading text-lg md:text-xl text-white italic leading-relaxed mb-6">
                  "{testimonial.quote}"
                </blockquote>

                {/* Footer */}
                <div className="pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-neon-pink text-sm font-medium">{testimonial.type}</p>
                      <p className="text-gray-500 text-xs">{testimonial.service}</p>
                    </div>
                    {testimonial.verified && (
                      <div className="flex items-center gap-1 text-xs text-neon-cyan">
                        <Shield className="w-3 h-3" />
                        Verified
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-16 bg-vip-black border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "100%", label: "Patient Satisfaction" },
              { value: "5.0", label: "Average Rating" },
              { value: "10K+", label: "Procedures" },
              { value: "100%", label: "Privacy Maintained" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold gradient-text mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Quote */}
      <section className="py-24 md:py-32 bg-vip-paper relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(0, 240, 255, 0.1) 0%, transparent 60%)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="mb-8">
            <Quote className="w-16 h-16 mx-auto text-royal-purple/30" />
          </div>
          <blockquote className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white italic leading-tight mb-8">
            "Best anesthesia experience I've ever had. Professional, caring, and I felt completely safe."
          </blockquote>
          <div className="flex items-center justify-center gap-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 text-neon-cyan fill-neon-cyan" />
            ))}
          </div>
          <p className="text-gray-400 mt-4">— Verified Patient</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-vip-black">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold italic text-white mb-6">
            Schedule Your <span className="gradient-text">Consultation</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg mb-10">
            Experience compassionate, expert anesthesia care for your next procedure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/appointments">
              <Button
                data-testid="testimonials-book-btn"
                className="shimmer-btn rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-10 py-6 text-base shadow-[0_0_30px_rgba(255,255,255,0.4)] transition-all duration-300"
              >
                Schedule Consultation
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="testimonials-contact-btn"
                variant="outline"
                className="rounded-full border border-white/20 bg-transparent text-white hover:border-neon-pink hover:text-neon-pink uppercase tracking-widest px-10 py-6 text-base transition-all duration-300"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
